import { FC } from 'react';
import { Edit, EditProps } from 'react-admin';
import { UpdateChannelPutForm } from './components';

export const UpdateChannelEdit: FC<EditProps> = () => {
  return (
    // `pessimistic` is what makes a server-side validation error reach its input: react-admin only
    // sets `returnPromise` on the update mutation in this mode, so `save()` can return the 422 body
    // for `setSubmissionErrors`. In the default `undoable` mode the save resolves before the request
    // is sent, the form redirects, and the duplicate-name 422 on `name` is lost to a generic
    // notification (TVR-1351).
    //
    // It is the majority pattern here, not the universal one: 13 of the 21 Edit views under
    // `src/resources` set it, this one included. The other 8 — CommandEdit, CommandParameterEdit,
    // CountryEdit, HardwareEdit, PartnerEdit, RoleEdit, SysConfigEdit and UserEdit (Country and
    // Partner via `<EditBase>`, which takes the same prop) — carry the same latent defect, so read
    // a missing prop there as unfixed rather than as a considered choice.
    //
    // Accepted regression (TVR-1351), verified against the installed ra-core 5.15.0 and
    // react-hook-form 7.75.0: for a 422 naming a field with no mounted input, pessimistic is worse
    // than undoable. `useEditController`'s default `onError` drops its notification when
    // `body.errors` is set and the mode is pessimistic, while `setSubmissionErrors` still calls
    // `setError` for a name nothing is registered under. So the server's message renders nowhere,
    // no input can ever clear it, the user gets only the generic `ra.message.invalid_form` from
    // `useNotifyIsFormInvalid`, and — worse — that leftover error keeps `formState.errors` non-empty,
    // so react-hook-form skips the submit handler on every later attempt and the form cannot be
    // re-sent until it is remounted. Undoable mode showed the same generic toast but left the form
    // usable. The window is narrow: `software_release_id`, validated by both requests and bound to
    // no input here, and `hardware_revision_id` while `hardware_id` is empty, since its input lives
    // inside a `FormDataConsumer`. The fix is the `partitionServerErrors` pattern in
    // `src/resources/software-releases/SoftwareReleaseEdit.tsx`; deliberately out of scope here.
    <Edit mutationMode="pessimistic">
      <UpdateChannelPutForm />
    </Edit>
  );
};
